import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { VStack, Button } from '@vivaahealth/design-system';

import * as api from '../../api';
import AppointmentDetailsCard from './AppointmentDetailsCard';
import AlertMessage from './AlertMessage';
import ConfirmationDialog from './ConfirmationDialog';
import Heading from './Heading';
import Layout from './Layout';
import Loading from './Loading';

import { getProviderNameFriendly } from '../../utils/provider';
import { useDigitalCheckIn } from './hooks/useDigitalCheckIn';
import { useAsyncFlow } from '../../hooks/common/useAsyncFlow';
import { useTheme } from '../../hooks/common/useTheme';

const DigitalCheckIn: React.FC = () => {
  const { t } = useTranslation('digitalCheckIn');
  const { encounterId, authCode } = useParams<{
    encounterId: string;
    authCode: string;
  }>();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const { isThemeLoading } = useTheme({ encounterId });

  const {
    error: fetchError,
    pending: isFetchingDetails,
    result: digitalCheckInDetails,
    execute: fetchDetails,
  } = useAsyncFlow(async () => {
    return await api.client.Previsit.fetchDigitalCheckInDetails({
      authCode,
      encounterId,
    });
  });

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authCode, encounterId]);

  const { isCheckingIn, isValidStatus } = useDigitalCheckIn(
    digitalCheckInDetails?.appointmentStatus,
  );

  const {
    error: checkInError,
    execute: checkInEncounter,
    pending: isProcessingCheckIn,
  } = useAsyncFlow(async () => {
    await api.client.Previsit.digitalCheckInEncounter({
      encounterId,
      authCode,
    });
    await fetchDetails();
  });

  const handleOnClick = () => {
    if (
      digitalCheckInDetails &&
      digitalCheckInDetails?.practicePreference
        .digitalArrivalIdentificationConfirmationRequired
    ) {
      setIsConfirmationDialogOpen(true);
    } else {
      checkInEncounter();
    }
  };

  if (isProcessingCheckIn) {
    return (
      <Layout>
        <Loading
          loadingHeader={t('checkInLoadingHeader')}
          loadingSubheader={t('checkInLoadingSubheader')}
        />
      </Layout>
    );
  }

  if (
    digitalCheckInDetails?.appointmentStatus === undefined &&
    (isFetchingDetails || isThemeLoading)
  ) {
    return (
      <Layout>
        <Loading loadingHeader={t('loadingHeader')} />
      </Layout>
    );
  }

  if (fetchError || checkInError) {
    const { header, subheader } = determineErrorMessage(
      fetchError || checkInError,
    );
    return (
      <Layout>
        <AlertMessage
          header={t(header)}
          subheader={t(subheader)}
          variant="danger"
        />
      </Layout>
    );
  }

  if (!isValidStatus) {
    return (
      <Layout>
        <AlertMessage
          header={t('errorHeader')}
          subheader={t('errorSubheader')}
          variant="danger"
        />
      </Layout>
    );
  }

  if (!digitalCheckInDetails) {
    return <Layout />;
  }

  const headerText = isCheckingIn
    ? t('checkInHeader')
    : t('arrivedHeader', {
        firstName:
          digitalCheckInDetails.patient.preferredFirstName ||
          digitalCheckInDetails.patient.firstName,
        providerDisplayName: getProviderNameFriendly(
          digitalCheckInDetails.provider,
          ['firstName', 'lastName', 'suffix'],
        ),
      });
  const subHeaderText = isCheckingIn
    ? t('checkInSubheader')
    : t('arrivedSubheader');
  const customContent =
    (isCheckingIn &&
      digitalCheckInDetails.practicePreference.digitalArrivalCustomContent
        ?.en) ||
    undefined;

  return (
    <Layout>
      {isConfirmationDialogOpen && (
        <ConfirmationDialog
          title={t('confirmationTitle')}
          description={t('confirmationDescription')}
          isOpen={isConfirmationDialogOpen}
          onClose={() => setIsConfirmationDialogOpen(false)}
          onConfirm={checkInEncounter}
        />
      )}
      <VStack gap={24}>
        <Heading
          header={headerText}
          subheader={subHeaderText}
          customContent={customContent}
        />
        <AppointmentDetailsCard
          {...digitalCheckInDetails}
          isCheckingIn={isCheckingIn}
        />
        {isCheckingIn && (
          <VStack paddingY={8}>
            <Button
              variant="primary"
              size="medium"
              fullWidth
              onClick={handleOnClick}
            >
              {t('confirmAndCheckIn')}
            </Button>
          </VStack>
        )}
      </VStack>
    </Layout>
  );
};

export default DigitalCheckIn;

const FailureReason = api.DigitalCheckIn.DigitalCheckInFailureReason;
interface ErrorMessage {
  header: string;
  subheader: string;
}

const determineErrorMessage = (error: Error | undefined): ErrorMessage => {
  const failureReason = determineFailureReason(error);
  switch (failureReason) {
    case FailureReason.AuthCodeExpired:
      return {
        header: 'expiredHeader',
        subheader: 'expiredSubheader',
      };
    default:
      return {
        header: 'errorHeader',
        subheader: 'errorSubheader',
      };
  }
};

const determineFailureReason = (
  error: Error | undefined,
): api.DigitalCheckIn.DigitalCheckInFailureReason => {
  if (!error) return FailureReason.Unknown;
  try {
    const errorMessage = JSON.parse(error.message);
    const errorMessageFailure = errorMessage?.message;
    if (
      Object.values(api.DigitalCheckIn.DigitalCheckInFailureReason).some(
        (failureReason) => failureReason === errorMessageFailure,
      )
    ) {
      return errorMessageFailure as api.DigitalCheckIn.DigitalCheckInFailureReason;
    }
  } catch (err) {
    console.log(err);
  }
  return FailureReason.Unknown;
};
